import * as React from 'react';
import withStyles from 'react-jss';
import { useState } from 'react';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Icon from 'antd/lib/icon';
import { SETask } from 'src/dto/response/fetchShipmentTasks';
import { StylesProps } from 'src/Library/Types';
import { getStylesForLabel } from './SETasksUtils';
import { Modal } from 'antd';

const styles = theme => ({
  layer2Style: {
    position: 'relative',
    zIndex: 2,
    background: 'white',
    height: '100%',
  },
  extrasHeader: {
    borderBottom: '1px solid #F7F7F7',
    fontWeight: 'bold',
    fontSize: theme.fontSize.h3,
    color: theme.colors.text,
    lineHeight: 1,
    padding: theme.spacing.md,
    height: 48,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
});

interface SEModalWrapperProps extends StylesProps<ReturnType<typeof styles>> {
  taskFieldComponent: React.ReactElement;
  taskData: SETask | undefined;
  editable: boolean;
  handleCloseAddTaskDetailsModal: (arg: boolean) => void;
  renderEditIcon: () => React.ReactElement;
  renderColDeadline: (taskData: SETask) => React.ReactElement;
  isVisible: boolean;
  showApproveActions: boolean;
  currentShipmentMilestone: string;
  isShipmentOnHold: boolean;
}

const SEModalWrapper: React.FunctionComponent<SEModalWrapperProps> = ({
  taskFieldComponent,
  classes,
  taskData,
  editable,
  handleCloseAddTaskDetailsModal,
  renderEditIcon,
  renderColDeadline,
  isVisible,
  showApproveActions,
  currentShipmentMilestone,
  isShipmentOnHold,
}) => {  
  const [markSelectedTaskDone, setMarkSelectedTaskDone] = useState<boolean>(true);
  const [taskUpdateActions, setTaskUpdateActions] = useState<'UPDATE' | 'APPROVE' | 'REJECT' | undefined>();  
  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false);
  const [isUpdateDisabled, setIsUpdateDisabled] = useState<boolean>(false);
  const [isApproveLoading, setIsApproveLoading] = useState<boolean>(false);
  const [isRejectLoading, setIsRejectLoading] = useState<boolean>(false);

  if (!isVisible) {
    return null;
  }
  
  const renderHeader = () => {
    const renderHeaderLeft = () => {
      return (<>
        <Icon
          style={{ marginRight: 8 }}
          type="cross"
          onClick={() => handleCloseAddTaskDetailsModal(false)}
        />
        <span style={{ marginRight: 8 }}>
          {taskData?.name}
        </span>
        <span style={{ marginRight: 24 }}>
          {renderEditIcon()}
        </span>
        <span>
          <span style={{ marginRight: 4, fontSize: 10, fontWeight: 400, color: '#333333' }}>Deadline:</span>
          {taskData && renderColDeadline(taskData)}
        </span>
      </>);
    }

    const handleShipmentSailingScheduleUpdate = () => {
      Modal.confirm({
        title: 'Confirmation Request',
        content: 'You have selected a different sailing schedule. Do you want to proceed with the new schedule?',
        okText: "Yes, Proceed",
        onOk: async () => setTaskUpdateActions('UPDATE'),
        onCancel: () => { },
      });
    };
    
    const renderUpdateTaskButton = () => {
      const onHold = currentShipmentMilestone === 'ON_HOLD';
      return (
        <Button
          style={{ background: onHold || isUpdateDisabled ? 'lightgrey' : '#006EC3', color: '#FFFFFF'}}
          disabled={onHold || isUpdateDisabled}
          onClick={() => {
            if ( taskData?.status === 'In Progress' && taskData?.code === 'SMT12' ){
              handleShipmentSailingScheduleUpdate();
            } else {
              setTaskUpdateActions('UPDATE');
            }
          }}
          loading={isUpdateLoading}
        >
          Submit
        </Button>
      );
    }
  
    const renderApprovalTaskButtons = () => {
      return (
        <>
          <Button
            disabled={isApproveLoading}
            key="reject"
            type="danger"
            ghost
            style={{ marginRight: 8 }}
            onClick={() => setTaskUpdateActions('REJECT')}
            loading={isRejectLoading}
          >
            Reject
          </Button>
          <Button
              disabled={isRejectLoading}
              key="approve"
              type="primary"
              style={{ backgroundColor: '#0F8E4E', border: 0 }}
              onClick={() => setTaskUpdateActions('APPROVE')}
              loading={isApproveLoading}
            >
              Approve
          </Button>
        </>
      );
    }

    const renderApprovalStatus = (approvalStatus) => {
      const labelStyles = getStylesForLabel(approvalStatus);
      return (
        <Button
          shape="round"
          style={{
            ...labelStyles.styles,
            height: 24,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'default',
          }}>
          {labelStyles.text}
        </Button>
      );
    }

    const renderHeaderRight = () => {
      if (isShipmentOnHold) {
        return;
      }

      if (editable) {  
        return (
          <>
            <Checkbox
              style={{ lineHeight: 2, fontSize: 10, fontWeight: 400, color: '#333333' }}
              checked={markSelectedTaskDone}
              onChange={() => setMarkSelectedTaskDone(!markSelectedTaskDone)}>
              Mark this task as done automatically?
            </Checkbox>
            {renderUpdateTaskButton()}
          </>
        )
      } else if (showApproveActions) {
        return renderApprovalTaskButtons();
      } else if (taskData?.approvalStatus) {
        return renderApprovalStatus(taskData?.approvalStatus);
      }
      return null;
    }

    return (<>
      <div>
        {renderHeaderLeft()}
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {renderHeaderRight()}
      </div>
    </>);
  }

  return <div className={classes.layer2Style}>
    <div className={classes.extrasHeader}>
      {renderHeader()}
    </div>
    <div style={{ padding: '24px 8px 78px 8px' }}>
      {taskFieldComponent && React.cloneElement(taskFieldComponent, {
          markDone: markSelectedTaskDone,
          taskUpdateActions,
          setTaskUpdateActions,
          setIsUpdateLoading,
          setIsApproveLoading,
          setIsRejectLoading,
          setIsUpdateDisabled,
          isRejectLoading,
      })}
    </div>
  </div>;
}

export default withStyles(styles)(SEModalWrapper);